import { apiFetch } from '@/api/request'
import type { ApiResponse } from '@/types/verification'

/**
 * 함우리몰 주문 API.
 *
 * 새 주문은 토스페이먼츠만 받는다.
 * 이전에 계좌이체로 접수된 주문은 결제수단을 그대로 둔다.
 */
export type MallOrderStatus = 'PENDING' | 'PAID' | 'CANCELED'

export const MALL_ORDER_STATUS_LABEL: Record<MallOrderStatus, string> = {
  PENDING: '입금 대기',
  PAID: '결제 확인',
  CANCELED: '주문 취소',
}

/** 구매 기관 화면용. 「입금했어요」 후에는 미입금처럼 보이지 않게 한다. */
export function mallBuyerOrderStatusLabel(order: {
  status: MallOrderStatus
  payNotifiedAt?: string | null
  payMethod?: string
}): string {
  if (order.status === 'PENDING' && order.payMethod === 'TOSS') {
    return '결제 대기'
  }
  if (order.status === 'PENDING' && order.payNotifiedAt) {
    return '입금 확인 중'
  }
  if (order.status === 'PAID' && order.payMethod === 'TOSS') {
    return '결제 완료'
  }
  return MALL_ORDER_STATUS_LABEL[order.status]
}

export function mallPayMethodLabel(order: { payMethod?: string; pgMethod?: string }): string {
  if (order.payMethod === 'TOSS') {
    const method = String(order.pgMethod || '').trim()
    return method ? `카드·간편결제 · ${method}` : '카드·간편결제'
  }
  if (!order.payMethod || order.payMethod === 'TRANSFER') return '계좌이체'
  return order.payMethod
}

/** 결제 금액과 항목 환불의 관계. 잔액은 환불 완료만 뺀다. 환불 대기는 아직 결제 쪽에 남아 있다. */
export function mallOrderRefundTotals(
  items: Array<{ refundStatus?: string | null; refundAmount?: number | null }> = [],
) {
  let refunded = 0
  let refundPending = 0
  for (const item of items) {
    const amount = Math.max(0, Math.round(Number(item.refundAmount) || 0))
    if (item.refundStatus === 'DONE') refunded += amount
    else if (item.refundStatus === 'PENDING') refundPending += amount
  }
  return { refunded, refundPending }
}

export type MallOrderItemStatus =
  | 'WAITING'
  | 'PAID'
  | 'READY'
  | 'SHIPPING'
  | 'DELIVERED'
  | 'DONE'
  | 'CANCEL_REQUEST'
  | 'RETURN_REQUEST'
  | 'CANCELED'

export const MALL_ORDER_ITEM_STATUS_LABEL: Record<MallOrderItemStatus, string> = {
  WAITING: '입금 대기',
  PAID: '신규 주문',
  READY: '발송 준비',
  SHIPPING: '배송중',
  DELIVERED: '배송완료',
  DONE: '구매확정',
  CANCEL_REQUEST: '취소 요청',
  RETURN_REQUEST: '반품 요청',
  CANCELED: '취소',
}

/** 구매 기관에게 보이는 주문 항목. 원가와 마진은 실려 오지 않는다. */
export interface MallOrderShipment {
  idx: number
  itemIdx: number
  orderCode: string
  sellerCode: string
  kind: 'INITIAL' | 'ADDITIONAL'
  courier: string
  invoice: string
  qty: number
  memo: string
  createdAt: string | null
  /** 이 송장의 개별 배송 추적 상태 (본발송/재발송 각각) */
  trackStatus?: string
  /** 위 상태가 발생한 시각(택배사 이벤트 시각) */
  trackAt?: string | null
  /** 마지막으로 우리가 조회한 시각 */
  trackCheckedAt?: string | null
  /** 이 송장이 배송완료로 판정된 시각. 있으면 완료 */
  deliveredAt?: string | null
}

export interface MallOrderItem {
  idx: number
  orderCode: string
  productCode: string
  sellerCode: string
  storeName: string
  name: string
  option: string
  image: string
  unit: string
  price: number
  qty: number
  amount: number
  /** 이 상품에 배분된 출고 배송비. 그 업체 마지막 취소 때 환불에 포함된다. */
  shippingShare?: number
  /** 주문 시점 기준. 이 수량마다 환불 배송비 1회. 넘치면 올림 */
  returnEvery?: number
  /** 주문 시점 기준 수량마다 받는 환불 배송비 */
  returnUnitFee?: number
  /** 주문 시점 규칙으로 계산한 환불 배송비. 단순 변심 반품 때 이 금액을 뺀다. */
  returnFee?: number
  /** 주문 시점 출고 소요 */
  leadTime?: string
  /** 주문 시점 판매 단위 EACH|BOX */
  sellUnit?: string
  /** 주문 시점 당일 출고 마감 */
  cutoff?: string
  /** 주문 시점 판매업체 교환·반품 안내 */
  returnPolicy?: string
  /** CHANGE 단순 변심 / FAULT 하자·오배송 */
  returnCause?: '' | 'CHANGE' | 'FAULT' | string
  /** 요청 때 확정된 반품비. 단순 변심이면 판매자 정산으로 들어간다. */
  appliedReturnFee?: number
  status: MallOrderItemStatus
  courier: string
  invoice: string
  trackStatus?: string
  trackAt?: string | null
  shippedAt: string | null
  deliveredAt: string | null
  confirmedAt?: string | null
  claimType?: string
  claimReason?: string
  claimRejectReason?: string
  claimPrevStatus?: string
  claimAt?: string | null
  claimResolvedAt?: string | null
  refundAmount?: number
  refundStatus?: 'NONE' | 'PENDING' | 'DONE' | string
  refundBank?: string
  refundAccount?: string
  refundHolder?: string
  refundAt?: string | null
  refundMemo?: string
  shipments?: MallOrderShipment[]
  extraShipCount?: number
}

/** 관리자·판매업체에게만 보이는 수익 정보가 붙은 항목 */
export interface MallOrderItemWithMargin extends MallOrderItem {
  cost: number
  marginShare: number
  platformMargin: number
  sellerMargin: number
  settlement: number
  settledAt?: string | null
  settledBy?: string
}

export interface MallOrder {
  code: string
  memberCode: string
  orgName: string
  receiver: string
  phone: string
  zipcode: string
  address: string
  addressDetail: string
  memo: string
  /** 기관 발주/내부 관리번호 */
  poNumber?: string
  itemAmount: number
  shippingFee: number
  totalAmount: number
  payMethod: string
  /** 토스 승인 후 실제 수단. 카드, 간편결제, 계좌이체 */
  pgMethod?: string
  depositor: string
  payNotifiedAt?: string | null
  taxInvoice?: boolean
  bizNum?: string
  bizName?: string
  /** 요청 시점에 저장된 사업자등록증 URL */
  bizCert?: string
  /** 세금계산서 수신 이메일 */
  taxEmail?: string
  /** 발행 완료된 세금계산서 문서 URL */
  taxDocUrl?: string
  /** NONE | REQUESTED | DONE */
  taxStatus?: 'NONE' | 'REQUESTED' | 'DONE' | string
  /** 발행 완료 시각 (DONE일 때 mo_tax_at) */
  taxAt?: string | null
  /** 관리자용 — 회원 프로필 이메일 (주문 스냅샷 없을 때 참고) */
  memberEmail?: string
  /** 관리자용 — 회원 프로필 사업자등록증 URL */
  memberBizCert?: string
  /** 관리자 반려 사유 (최근) */
  taxRejectReason?: string
  status: MallOrderStatus
  paidAt: string | null
  paidBy: string
  cancelReason: string
  orderedAt: string
  itemCount: number
  itemSummary: string
  /** 목록용 항목 상태 요약 (배송·클레임 단계) */
  itemStatuses?: MallOrderItemStatus[]
  /** 목록용 상품코드. itemStatuses 와 같은 순서 */
  itemProductCodes?: string[]
  /** 송장이 있는 항목. 목록에서 바로 배송조회할 때 쓴다. */
  trackItems?: Array<{
    idx: number
    courier: string
    invoice: string
    name: string
  }>
  items?: MallOrderItem[]
}

export interface AdminMallOrder extends Omit<MallOrder, 'items'> {
  /** 관리자 내부 메모 — 구매자에게는 내려가지 않는다 */
  adminMemo?: string
  items?: MallOrderItemWithMargin[]
  /** 수동 환불 대기 건수 */
  refundPendingCount?: number
  /** 수동 환불 완료 건수 */
  refundDoneCount?: number
  /** 이 주문에 달린 미답변 상품문의 */
  qnaOpenCount?: number
  openQnas?: AdminOrderQna[]
}

export interface AdminOrderQna {
  idx: number
  productCode: string
  productName: string
  question: string
  createdAt: string | null
}

/** 판매업체 주문 목록 한 줄 — 배송에 필요한 주문 정보가 함께 온다. */
export interface SellerOrderItem extends MallOrderItemWithMargin {
  buyerOrg: string
  receiver: string
  phone: string
  address: string
  memo: string
  poNumber?: string
  orderedAt: string
  paidAt: string | null
  payMethod?: string
  pgMethod?: string
  /** 이 주문에 걸린, 판매업체 본인 상품에 대한 미답변 상품문의 수 */
  qnaOpenCount?: number
}

export const MALL_TAX_STATUS_LABEL: Record<string, string> = {
  NONE: '미요청',
  REQUESTED: '발행 요청',
  DONE: '발행 완료',
}

export interface MallPaymentInfo {
  bankName: string
  account: string
  holder: string
  notice: string
  /** 몰 홈 상단 운영 공지 (비우면 배너 숨김) */
  opsNotice?: string
}

export interface OrderItemInput {
  productId: string
  qty: number
  optionName?: string
  optionExtra?: number
  name?: string
  price?: number
  image?: string
  unit?: string
}

export interface CreateOrderPayload {
  receiver: string
  phone: string
  email?: string
  zipcode?: string
  address: string
  addressDetail?: string
  memo?: string
  poNumber?: string
  depositor?: string
  /** 새 주문은 TOSS. TRANSFER 는 이전 계좌이체 주문 기록이다. */
  payMethod?: 'TOSS' | 'TRANSFER'
  shippingFee: number
  taxInvoice?: boolean
  bizNum?: string
  bizName?: string
  items: OrderItemInput[]
}

const API_ORIGIN = (process.env.VUE_APP_API_ORIGIN || '').replace(/\/$/, '')
const MALL_BASE = `${API_ORIGIN}/api/mall`
const SELLER_BASE = `${API_ORIGIN}/api/mall/seller`
const ADMIN_BASE = `${API_ORIGIN}/api/admin/mall`

const fetchOptions: RequestInit = { credentials: 'include' }
const jsonOptions: RequestInit = {
  ...fetchOptions,
  headers: { 'Content-Type': 'application/json' },
}

async function parseJson<T>(response: Response): Promise<ApiResponse<T>> {
  const contentType = response.headers.get('content-type') || ''
  if (!contentType.includes('application/json')) {
    throw new Error('API 서버에 연결할 수 없습니다. 백엔드와 프론트 dev 서버를 확인해 주세요.')
  }

  const data = (await response.json()) as ApiResponse<T>
  if (!response.ok && !data.message) {
    throw new Error('요청 처리에 실패했습니다.')
  }
  return data
}

function unwrap<T>(result: ApiResponse<T>, fallback: string): T {
  if (!result.success || !result.data) {
    throw new Error(result.message || fallback)
  }
  return result.data
}

/* ---------------- 구매 기관 ---------------- */

export interface TossCheckoutConfig {
  clientKey: string
  customerKey: string
  testMode: boolean
}

export async function fetchTossAccess() {
  const response = await apiFetch(`${MALL_BASE}/toss-access`, fetchOptions)
  const result = await parseJson<{ enabled: boolean }>(response)
  return unwrap(result, '결제 수단을 확인하지 못했습니다.').enabled
}

export async function fetchTossCheckout() {
  const response = await apiFetch(`${MALL_BASE}/toss-checkout`, fetchOptions)
  const result = await parseJson<TossCheckoutConfig>(response)
  return unwrap(result, '결제창을 준비하지 못했습니다.')
}

export async function confirmTossPayment(
  code: string,
  payload: { paymentKey: string; amount: number },
) {
  const response = await apiFetch(`${MALL_BASE}/orders/${encodeURIComponent(code)}/toss-confirm`, {
    ...jsonOptions,
    method: 'POST',
    body: JSON.stringify(payload),
  })
  const result = await parseJson<{ order: MallOrder }>(response)
  return unwrap(result, result.message || '결제 승인에 실패했습니다.')
}

export async function fetchPaymentInfo() {
  const response = await apiFetch(`${MALL_BASE}/payment-info`, fetchOptions)
  const result = await parseJson<{ payment: MallPaymentInfo }>(response)
  return unwrap(result, '입금 정보를 불러오지 못했습니다.').payment
}

export async function createMallOrder(payload: CreateOrderPayload) {
  const response = await apiFetch(`${MALL_BASE}/orders`, {
    ...jsonOptions,
    method: 'POST',
    body: JSON.stringify(payload),
  })
  const result = await parseJson<{ order: MallOrder; payment: MallPaymentInfo }>(response)
  return unwrap(result, '주문에 실패했습니다.')
}

export async function fetchMyOrders(query: { from?: string; to?: string; keyword?: string } = {}) {
  const params = new URLSearchParams()
  if (query.from) params.set('from', query.from)
  if (query.to) params.set('to', query.to)
  if (query.keyword) params.set('keyword', query.keyword)
  const suffix = params.toString() ? `?${params.toString()}` : ''
  const response = await apiFetch(`${MALL_BASE}/orders${suffix}`, fetchOptions)
  const result = await parseJson<{ orders: MallOrder[] }>(response)
  return unwrap(result, '주문 목록을 불러오지 못했습니다.').orders
}

export async function fetchLastPurchaseQty(productCode: string) {
  const response = await apiFetch(
    `${MALL_BASE}/products/${encodeURIComponent(productCode)}/last-purchase`,
    fetchOptions,
  )
  const result = await parseJson<{ qty: number | null }>(response)
  return unwrap(result, '구매 이력을 불러오지 못했습니다.').qty
}

export async function saveAdminTaxStatus(
  code: string,
  taxStatus: string,
  biz: { bizNum?: string; bizName?: string; taxDocUrl?: string } = {},
) {
  const response = await apiFetch(
    `${ADMIN_BASE}/orders/${encodeURIComponent(code)}/tax-status`,
    {
      ...jsonOptions,
      method: 'PATCH',
      body: JSON.stringify({
        taxStatus,
        bizNum: biz.bizNum,
        bizName: biz.bizName,
        taxDocUrl: biz.taxDocUrl,
      }),
    },
  )
  const result = await parseJson<{ taxStatus: string; order: AdminMallOrder }>(response)
  return unwrap(result, '세금계산서 상태를 저장하지 못했습니다.')
}

export async function rejectAdminTaxInvoice(
  code: string,
  payload: {
    issues?: string[]
    reason?: string
    smsMessage?: string
    sendSms?: boolean
  } = {},
) {
  const response = await apiFetch(
    `${ADMIN_BASE}/orders/${encodeURIComponent(code)}/tax-reject`,
    {
      ...jsonOptions,
      method: 'POST',
      body: JSON.stringify(payload),
    },
  )
  const result = await parseJson<{
    rejectReason: string
    issues: string[]
    smsSent: boolean
    order: AdminMallOrder
  }>(response)
  const data = unwrap(result, '세금계산서 발행 요청을 반려하지 못했습니다.')
  return { ...data, message: result.message || '세금계산서 발행 요청을 반려했습니다.' }
}

/** 최근 구매 상품 — 홈 '다시 살 상품' 레일 */
export async function fetchBuyAgainProducts(limit = 8) {
  const params = new URLSearchParams({ limit: String(limit) })
  const response = await apiFetch(`${MALL_BASE}/buy-again?${params}`, fetchOptions)
  const result = await parseJson<{ products: import('@/api/mallProduct').ServerMallProduct[] }>(
    response,
  )
  return unwrap(result, '구매 이력을 불러오지 못했습니다.').products
}

export async function fetchMyOrder(code: string) {
  const response = await apiFetch(`${MALL_BASE}/orders/${encodeURIComponent(code)}`, fetchOptions)
  const result = await parseJson<{ order: MallOrder; payment: MallPaymentInfo }>(response)
  return unwrap(result, '주문을 불러오지 못했습니다.')
}

export async function cancelMyOrder(code: string) {
  const response = await apiFetch(`${MALL_BASE}/orders/${encodeURIComponent(code)}/cancel`, {
    ...jsonOptions,
    method: 'POST',
    body: JSON.stringify({}),
  })
  const result = await parseJson<null>(response)
  if (!result.success) {
    throw new Error(result.message || '주문을 취소하지 못했습니다.')
  }
}

/** 주문 후 세금계산서 발행 요청 (결제 때 안 한 경우) */
export async function requestMyTaxInvoice(
  code: string,
  payload: { bizNum: string; bizName: string; email?: string },
) {
  const response = await apiFetch(
    `${MALL_BASE}/orders/${encodeURIComponent(code)}/tax-invoice`,
    {
      ...jsonOptions,
      method: 'POST',
      body: JSON.stringify(payload),
    },
  )
  const result = await parseJson<{ order: MallOrder }>(response)
  return {
    order: unwrap(result, '세금계산서 요청에 실패했습니다.').order,
    message: result.message || '세금계산서 발행을 요청했습니다.',
  }
}

export async function notifyMyPayment(code: string) {
  const response = await apiFetch(
    `${MALL_BASE}/orders/${encodeURIComponent(code)}/notify-payment`,
    {
      ...jsonOptions,
      method: 'POST',
      body: JSON.stringify({}),
    },
  )
  const result = await parseJson<{ notifiedAt: string | null; order: MallOrder }>(response)
  return {
    ...unwrap(result, '입금 알림에 실패했습니다.'),
    message: result.message ?? '',
  }
}

/* ---------------- 관리자 ---------------- */

export type OrderStatusCounts = Partial<Record<MallOrderStatus, number>> & {
  taxInvoicePending?: number
}

export async function fetchAdminOrders(
  query: { status?: string; keyword?: string; from?: string; to?: string; tax?: boolean } = {},
) {
  const params = new URLSearchParams()
  if (query.status) params.set('status', query.status)
  if (query.keyword) params.set('keyword', query.keyword)
  if (query.from) params.set('from', query.from)
  if (query.to) params.set('to', query.to)
  if (query.tax) params.set('tax', '1')

  const suffix = params.toString() ? `?${params.toString()}` : ''
  const response = await apiFetch(`${ADMIN_BASE}/orders${suffix}`, fetchOptions)
  const result = await parseJson<{ orders: AdminMallOrder[]; counts: OrderStatusCounts }>(response)
  return unwrap(result, '주문 목록을 불러오지 못했습니다.')
}

export async function fetchAdminOrder(code: string) {
  const response = await apiFetch(`${ADMIN_BASE}/orders/${encodeURIComponent(code)}`, fetchOptions)
  const result = await parseJson<{ order: AdminMallOrder }>(response)
  return unwrap(result, '주문을 불러오지 못했습니다.').order
}

export async function saveAdminOrderMemo(code: string, memo: string) {
  const response = await apiFetch(`${ADMIN_BASE}/orders/${encodeURIComponent(code)}/memo`, {
    ...jsonOptions,
    method: 'PATCH',
    body: JSON.stringify({ memo }),
  })
  const result = await parseJson<{ memo: string; order: AdminMallOrder }>(response)
  return unwrap(result, '메모를 저장하지 못했습니다.')
}

export async function confirmOrderPayment(code: string) {
  const response = await apiFetch(`${ADMIN_BASE}/orders/${encodeURIComponent(code)}/confirm`, {
    ...jsonOptions,
    method: 'POST',
    body: JSON.stringify({}),
  })
  const result = await parseJson<{ order: AdminMallOrder }>(response)
  return { order: unwrap(result, '결제확인에 실패했습니다.').order, message: result.message ?? '' }
}

export async function unconfirmOrderPayment(code: string) {
  const response = await apiFetch(`${ADMIN_BASE}/orders/${encodeURIComponent(code)}/unconfirm`, {
    ...jsonOptions,
    method: 'POST',
    body: JSON.stringify({}),
  })
  const result = await parseJson<{ order: AdminMallOrder }>(response)
  return { order: unwrap(result, '결제확인 취소에 실패했습니다.').order, message: result.message ?? '' }
}

export async function cancelOrderByAdmin(code: string, reason: string) {
  const response = await apiFetch(`${ADMIN_BASE}/orders/${encodeURIComponent(code)}/cancel`, {
    ...jsonOptions,
    method: 'POST',
    body: JSON.stringify({ reason }),
  })
  const result = await parseJson<null>(response)
  if (!result.success) {
    throw new Error(result.message || '주문을 취소하지 못했습니다.')
  }
}

export async function saveAdminPaymentInfo(payment: MallPaymentInfo) {
  const response = await apiFetch(`${ADMIN_BASE}/payment-info`, {
    ...jsonOptions,
    method: 'PATCH',
    body: JSON.stringify(payment),
  })
  const result = await parseJson<{ payment: MallPaymentInfo }>(response)
  return unwrap(result, '입금 계좌를 저장하지 못했습니다.').payment
}

export async function fetchAdminClaims(status = '') {
  const params = new URLSearchParams()
  if (status) params.set('status', status)
  const suffix = params.toString() ? `?${params.toString()}` : ''
  const response = await apiFetch(`${ADMIN_BASE}/claims${suffix}`, fetchOptions)
  const result = await parseJson<{ items: SellerClaimItem[]; pending: number }>(response)
  return unwrap(result, '클레임 목록을 불러오지 못했습니다.')
}

export type SettlementStatus = 'SCHEDULED' | 'PAID'

export interface AdminSettlementItem {
  id: string
  itemIdx: number
  orderCode: string
  sellerCode: string
  storeName: string
  productName: string
  optionLabel: string
  qty: number
  unit: string
  salesAmount: number
  platformFee: number
  sellerMargin: number
  shippingFee: number
  payout: number
  /** SALE 구매확정 / RETURN_FEE 단순 변심 반품비 / SHIPPING 반품 후 출고 배송비 */
  lineKind?: 'SALE' | 'RETURN_FEE' | 'SHIPPING'
  itemStatus: string
  orderedAt: string | null
  paidAt: string | null
  shippedAt: string | null
  deliveredAt: string | null
  confirmedAt?: string | null
  settledAt: string | null
  settledBy: string
  status: SettlementStatus
  settleBank?: string
  settleAccount?: string
  settleHolder?: string
}

/** @deprecated 월별 정산 헤더. 관리자 화면은 AdminSettlementItem 을 쓴다. */
export interface AdminSettlement extends SellerSettlement {
  sellerCode: string
  storeName: string
  settleBank?: string
  settleAccount?: string
  settleHolder?: string
}

export interface AdminSettlementPatch {
  payoutAt?: string
  closedAt?: string
  shipping?: number
  memo?: string
}

export type SettlementLineKind = 'SALE' | 'DEDUCTION' | 'CLAWBACK'

export interface AdminSettlementLine {
  id: string
  kind: SettlementLineKind
  itemIdx: number
  orderCode: string
  productName: string
  optionLabel: string
  qty: number
  unit: string
  amount: number
  platformFee: number
  settlement: number
  itemStatus: string
  orderedAt: string | null
  paidAt: string | null
  shippedAt: string | null
  deliveredAt: string | null
  claimAt: string | null
  claimResolvedAt: string | null
  claimType: string
  salePeriod: string
  salePeriodPaid: boolean
  settleStatus: SettlementStatus
  settlePaidAt: string
}

export interface AdminSettlementLinesResult {
  sellerCode: string
  period: string
  periodLabel: string
  settleStatus: SettlementStatus
  settlePaidAt: string
  lines: AdminSettlementLine[]
}

export async function fetchAdminSettlements(
  query: { period?: string; status?: string; keyword?: string } = {},
) {
  const params = new URLSearchParams()
  if (query.period) params.set('period', query.period)
  if (query.status) params.set('status', query.status)
  if (query.keyword) params.set('keyword', query.keyword)
  const suffix = params.toString() ? `?${params.toString()}` : ''
  const response = await apiFetch(`${ADMIN_BASE}/settlements${suffix}`, fetchOptions)
  const result = await parseJson<{
    items: AdminSettlementItem[]
    scheduled: number
    payoutSum: number
  }>(response)
  return unwrap(result, '정산 목록을 불러오지 못했습니다.')
}

export async function markAdminSettlementItemPaid(itemIdx: number) {
  const response = await apiFetch(`${ADMIN_BASE}/settlements/items/${itemIdx}/pay`, {
    ...jsonOptions,
    method: 'POST',
    body: JSON.stringify({}),
  })
  const result = await parseJson<{ item: AdminSettlementItem | null }>(response)
  if (!result.success) {
    throw new Error(result.message || '정산 처리에 실패했습니다.')
  }
  return result.message || '정산 처리했습니다.'
}

export async function reopenAdminSettlementItem(itemIdx: number, reason: string) {
  const response = await apiFetch(`${ADMIN_BASE}/settlements/items/${itemIdx}/reopen`, {
    ...jsonOptions,
    method: 'POST',
    body: JSON.stringify({ reason }),
  })
  const result = await parseJson<{ item: AdminSettlementItem | null }>(response)
  if (!result.success) {
    throw new Error(result.message || '정산 해제를 하지 못했습니다.')
  }
  return result.message || '정산을 해제했습니다.'
}

export async function markAdminSettlementItemsPaidBatch(idxs: number[]) {
  const response = await apiFetch(`${ADMIN_BASE}/settlements/items/batch-pay`, {
    ...jsonOptions,
    method: 'POST',
    body: JSON.stringify({ idxs }),
  })
  const result = await parseJson<{
    ok: AdminSettlementItem[]
    failed: Array<{ itemIdx: number; code: string }>
  }>(response)
  if (!result.success) {
    throw new Error(result.message || '일괄 정산 처리에 실패했습니다.')
  }
  return {
    message: result.message || '일괄 정산 처리했습니다.',
    ...(result.data || { ok: [], failed: [] }),
  }
}

export async function reopenAdminSettlementItemsBatch(idxs: number[], reason: string) {
  const response = await apiFetch(`${ADMIN_BASE}/settlements/items/batch-reopen`, {
    ...jsonOptions,
    method: 'POST',
    body: JSON.stringify({ idxs, reason }),
  })
  const result = await parseJson<{
    ok: AdminSettlementItem[]
    failed: Array<{ itemIdx: number; code: string }>
  }>(response)
  if (!result.success) {
    throw new Error(result.message || '일괄 정산 해제에 실패했습니다.')
  }
  return {
    message: result.message || '일괄 정산 해제했습니다.',
    ...(result.data || { ok: [], failed: [] }),
  }
}

export interface AdminSettlementLog {
  id: number
  itemIdx: number
  orderCode: string
  sellerCode: string
  action: 'SETTLE' | 'UNSETTLE'
  actor: string
  reason: string
  payout: number
  storeName: string
  productName: string
  at: string | null
}

export async function fetchAdminSettlementLogs(query: {
  itemIdx?: number
  orderCode?: string
  limit?: number
} = {}) {
  const params = new URLSearchParams()
  if (query.itemIdx) params.set('itemIdx', String(query.itemIdx))
  if (query.orderCode) params.set('orderCode', query.orderCode)
  if (query.limit) params.set('limit', String(query.limit))
  const suffix = params.toString() ? `?${params.toString()}` : ''
  const response = await apiFetch(`${ADMIN_BASE}/settlements/logs${suffix}`, fetchOptions)
  const result = await parseJson<{ logs: AdminSettlementLog[] }>(response)
  return unwrap(result, '정산 이력을 불러오지 못했습니다.').logs
}

export async function fetchAdminSettlementLines(sellerCode: string, period: string) {
  const params = new URLSearchParams()
  params.set('sellerCode', sellerCode)
  params.set('period', period)
  const response = await apiFetch(
    `${ADMIN_BASE}/settlements/lines?${params.toString()}`,
    fetchOptions,
  )
  const result = await parseJson<AdminSettlementLinesResult>(response)
  return unwrap(result, '정산 주문 내역을 불러오지 못했습니다.')
}

export async function saveAdminSettlementMeta(
  sellerCode: string,
  period: string,
  patch: AdminSettlementPatch,
) {
  const response = await apiFetch(`${ADMIN_BASE}/settlements`, {
    ...jsonOptions,
    method: 'PATCH',
    body: JSON.stringify({ sellerCode, period, ...patch }),
  })
  const result = await parseJson<{ settlement: SellerSettlement | null }>(response)
  if (!result.success) {
    throw new Error(result.message || '정산 상세를 저장하지 못했습니다.')
  }
  return result.message || '정산 상세를 저장했습니다.'
}

export async function markAdminSettlementPaid(
  sellerCode: string,
  period: string,
  patch: AdminSettlementPatch = {},
) {
  const response = await apiFetch(`${ADMIN_BASE}/settlements/pay`, {
    ...jsonOptions,
    method: 'POST',
    body: JSON.stringify({ sellerCode, period, ...patch }),
  })
  const result = await parseJson<{ settlement: SellerSettlement | null }>(response)
  if (!result.success) {
    throw new Error(result.message || '지급 완료 처리에 실패했습니다.')
  }
  return result.message || '지급 완료로 표시했습니다.'
}

export async function reopenAdminSettlement(
  sellerCode: string,
  period: string,
  reason: string,
) {
  const response = await apiFetch(`${ADMIN_BASE}/settlements/reopen`, {
    ...jsonOptions,
    method: 'POST',
    body: JSON.stringify({ sellerCode, period, reason }),
  })
  const result = await parseJson<{ settlement: SellerSettlement | null }>(response)
  if (!result.success) {
    throw new Error(result.message || '정산 상태를 되돌리지 못했습니다.')
  }
  return result.message || '지급 예정으로 되돌렸습니다.'
}

/* ---------------- 판매업체 ---------------- */

export type ItemStatusCounts = Partial<Record<MallOrderItemStatus, number>>

export async function fetchSellerOrders() {
  const response = await apiFetch(`${SELLER_BASE}/orders`, fetchOptions)
  const result = await parseJson<{ items: SellerOrderItem[]; counts: ItemStatusCounts }>(response)
  return unwrap(result, '주문 목록을 불러오지 못했습니다.')
}

export async function updateSellerOrderStatus(
  idx: number,
  status: MallOrderItemStatus,
  shipping: { courier?: string; invoice?: string } = {},
) {
  const response = await apiFetch(`${SELLER_BASE}/orders/${idx}/status`, {
    ...jsonOptions,
    method: 'PATCH',
    body: JSON.stringify({ status, ...shipping }),
  })
  const result = await parseJson<null>(response)
  if (!result.success) {
    throw new Error(result.message || '주문 상태를 바꾸지 못했습니다.')
  }
}

export async function createSellerExtraShipment(
  idx: number,
  payload: { courier: string; invoice?: string; qty?: number; memo?: string },
) {
  const response = await apiFetch(`${SELLER_BASE}/orders/${idx}/extra-ship`, {
    ...jsonOptions,
    method: 'POST',
    body: JSON.stringify(payload),
  })
  const result = await parseJson<{ shipment?: unknown }>(response)
  if (!result.success) {
    throw new Error(result.message || '추가발송을 등록하지 못했습니다.')
  }
  return result.message || '추가발송을 등록했습니다.'
}

/** 관리자가 판매업체 대신 추가발송을 등록한다. */
export async function createAdminExtraShipment(
  idx: number,
  payload: { courier: string; invoice?: string; qty?: number; memo?: string },
) {
  const response = await apiFetch(`${ADMIN_BASE}/orders/items/${idx}/extra-ship`, {
    ...jsonOptions,
    method: 'POST',
    body: JSON.stringify(payload),
  })
  const result = await parseJson<{ shipment?: unknown }>(response)
  if (!result.success) {
    throw new Error(result.message || '추가발송을 등록하지 못했습니다.')
  }
  return result.message || '추가발송을 등록했습니다.'
}

export type ClaimType = 'CANCEL' | 'RETURN'

export async function requestOrderItemClaim(
  idx: number,
  payload: {
    type: ClaimType
    reason: string
    returnCause?: 'CHANGE' | 'FAULT' | ''
    refundBank: string
    refundAccount: string
    refundHolder: string
  },
) {
  const response = await apiFetch(`${MALL_BASE}/order-items/${idx}/claim`, {
    ...jsonOptions,
    method: 'POST',
    body: JSON.stringify(payload),
  })
  const result = await parseJson<null>(response)
  if (!result.success) {
    throw new Error(result.message || '요청을 보내지 못했습니다.')
  }
  return result.message || '요청을 보냈습니다.'
}

export async function updateOrderItemRefundAccount(
  idx: number,
  payload: { refundBank: string; refundAccount: string; refundHolder: string },
) {
  const response = await apiFetch(`${MALL_BASE}/order-items/${idx}/refund-account`, {
    ...jsonOptions,
    method: 'PATCH',
    body: JSON.stringify(payload),
  })
  const result = await parseJson<null>(response)
  if (!result.success) {
    throw new Error(result.message || '환불 계좌를 저장하지 못했습니다.')
  }
  return result.message || '환불 계좌를 저장했습니다.'
}

export const MALL_REFUND_STATUS_LABEL: Record<string, string> = {
  NONE: '-',
  PENDING: '환불 대기',
  DONE: '환불 완료',
}

export async function confirmOrderItemPurchase(idx: number) {
  const response = await apiFetch(`${MALL_BASE}/order-items/${idx}/confirm`, {
    ...jsonOptions,
    method: 'POST',
    body: JSON.stringify({}),
  })
  const result = await parseJson<null>(response)
  if (!result.success) {
    throw new Error(result.message || '구매확정에 실패했습니다.')
  }
  return result.message || '구매를 확정했습니다.'
}

export interface MallTrackingEvent {
  at: string
  location: string
  status: string
  description: string
}

export interface MallTracking {
  courier: string
  invoice: string
  productName?: string
  orderCode?: string
  lastStatus: string
  pending: boolean
  direct: boolean
  delivered: boolean
  itemStatus?: MallOrderItemStatus
  message: string
  sourceUrl: string | null
  events: MallTrackingEvent[]
}

/**
 * 특정 주문 항목의 배송조회.
 * `invoice`(선택) 를 함께 넘기면 그 송장(본발송/재발송 중 하나) 을 조회하고, 결과가
 * 서버 측 caion_mall_order_shipment 의 개별 추적 컬럼에도 저장된다.
 */
export async function fetchOrderItemTracking(
  idx: number,
  options: { courier?: string; invoice?: string } = {},
) {
  const params = new URLSearchParams()
  const invoice = String(options.invoice || '').replace(/\s+/g, '')
  const courier = String(options.courier || '').trim()
  if (invoice) params.set('invoice', invoice)
  if (courier) params.set('courier', courier)
  const suffix = params.toString() ? `?${params.toString()}` : ''
  const response = await apiFetch(`${MALL_BASE}/order-items/${idx}/tracking${suffix}`, fetchOptions)
  const result = await parseJson<{ tracking: MallTracking }>(response)
  return unwrap(result, '배송조회를 불러오지 못했습니다.').tracking
}

/** 로그인 없이 택배사·송장으로 배송조회 */
export async function fetchPublicParcelTracking(courier: string, invoice: string) {
  const params = new URLSearchParams({
    courier: String(courier || '').trim(),
    invoice: String(invoice || '').replace(/\s+/g, ''),
  })
  const response = await apiFetch(`${MALL_BASE}/tracking?${params.toString()}`, {
    method: 'GET',
    credentials: 'omit',
  })
  const result = await parseJson<{ tracking: MallTracking }>(response)
  return unwrap(result, '배송조회를 불러오지 못했습니다.').tracking
}

/** 택배사별 조회 URL. 직접배송은 null, 미지원은 검색. */
export { publicTrackingUrl as trackingUrlOf } from '@/constants/mallCouriers'

export interface SellerClaimItem extends SellerOrderItem {
  buyerName: string
}

export async function fetchSellerClaims() {
  const response = await apiFetch(`${SELLER_BASE}/claims`, fetchOptions)
  const result = await parseJson<{ items: SellerClaimItem[]; pending: number }>(response)
  return unwrap(result, '클레임 목록을 불러오지 못했습니다.')
}

export async function approveSellerClaim(idx: number) {
  const response = await apiFetch(`${SELLER_BASE}/claims/${idx}/approve`, {
    ...jsonOptions,
    method: 'POST',
    body: JSON.stringify({}),
  })
  const result = await parseJson<null>(response)
  if (!result.success) {
    throw new Error(result.message || '승인에 실패했습니다.')
  }
  return result.message || '승인했습니다.'
}

export async function rejectSellerClaim(idx: number, reason: string) {
  const response = await apiFetch(`${SELLER_BASE}/claims/${idx}/reject`, {
    ...jsonOptions,
    method: 'POST',
    body: JSON.stringify({ reason }),
  })
  const result = await parseJson<null>(response)
  if (!result.success) {
    throw new Error(result.message || '반려에 실패했습니다.')
  }
  return result.message || '반려했습니다.'
}

export async function approveAdminClaim(idx: number) {
  const response = await apiFetch(`${ADMIN_BASE}/claims/${idx}/approve`, {
    ...jsonOptions,
    method: 'POST',
    body: JSON.stringify({}),
  })
  const result = await parseJson<null>(response)
  if (!result.success) {
    throw new Error(result.message || '승인에 실패했습니다.')
  }
  return result.message || '승인했습니다.'
}

export async function rejectAdminClaim(idx: number, reason: string) {
  const response = await apiFetch(`${ADMIN_BASE}/claims/${idx}/reject`, {
    ...jsonOptions,
    method: 'POST',
    body: JSON.stringify({ reason }),
  })
  const result = await parseJson<null>(response)
  if (!result.success) {
    throw new Error(result.message || '반려에 실패했습니다.')
  }
  return result.message || '반려했습니다.'
}

export interface AdminRefundItem extends SellerClaimItem {
  depositor?: string
  refundBy?: string
}

export async function fetchAdminRefunds(status = '') {
  const params = new URLSearchParams()
  if (status) params.set('status', status)
  const suffix = params.toString() ? `?${params.toString()}` : ''
  const response = await apiFetch(`${ADMIN_BASE}/refunds${suffix}`, fetchOptions)
  const result = await parseJson<{
    items: AdminRefundItem[]
    pending: number
    pendingAmount: number
  }>(response)
  return unwrap(result, '환불 목록을 불러오지 못했습니다.')
}

export type AdminAdjustAction = 'refund-done' | 'refund-pending' | 'status'

export async function adjustAdminOrderItem(
  idx: number,
  payload: {
    action: AdminAdjustAction
    status?: MallOrderItemStatus
    reason: string
    refundBank?: string
    refundAccount?: string
    refundHolder?: string
    memo?: string
  },
) {
  const response = await apiFetch(`${ADMIN_BASE}/orders/items/${idx}/adjust`, {
    ...jsonOptions,
    method: 'POST',
    body: JSON.stringify(payload),
  })
  const result = await parseJson<null>(response)
  if (!result.success) {
    throw new Error(result.message || '주문 조정에 실패했습니다.')
  }
  return result.message || '주문을 조정했습니다.'
}

export async function markAdminRefundDone(
  idx: number,
  payload: {
    memo?: string
    refundBank?: string
    refundAccount?: string
    refundHolder?: string
  } = {},
) {
  const response = await apiFetch(`${ADMIN_BASE}/refunds/${idx}/done`, {
    ...jsonOptions,
    method: 'POST',
    body: JSON.stringify(payload),
  })
  const result = await parseJson<null>(response)
  if (!result.success) {
    throw new Error(result.message || '환불 완료 처리에 실패했습니다.')
  }
  return result.message || '환불 완료로 표시했습니다.'
}

export interface SellerSettlement {
  id: string
  period: string
  periodLabel: string
  closedAt: string
  payoutAt: string
  orderCount: number
  /** 해당 월 구매확정 건 중 정산 처리된 건수 */
  settledCount?: number
  salesAmount: number
  platformFee: number
  sellerMargin: number
  deduction: number
  /** 원 매출월이 PAID 인 뒤의 반품 회수액 (지급액에 이미 반영) */
  clawback?: number
  shipping: number
  payout: number
  memo?: string
  payoutDateCustom?: boolean
  status: SettlementStatus
}

export async function fetchSellerSettlements(
  query: { period?: string; status?: string; keyword?: string } = {},
) {
  const params = new URLSearchParams()
  if (query.period) params.set('period', query.period)
  if (query.status) params.set('status', query.status)
  if (query.keyword) params.set('keyword', query.keyword)
  const suffix = params.toString() ? `?${params.toString()}` : ''
  const response = await apiFetch(`${SELLER_BASE}/settlements${suffix}`, fetchOptions)
  const result = await parseJson<{
    items: AdminSettlementItem[]
    pendingCount: number
    pendingSum: number
    paidCount?: number
    paidSum?: number
    periodCount?: number
    periodSum?: number
    totalPendingCount?: number
    totalPendingSum?: number
    totalPaidCount?: number
    totalPaidSum?: number
  }>(response)
  return unwrap(result, '정산 내역을 불러오지 못했습니다.')
}

export interface SettlementRequestItem {
  id: number
  orderCode: string
  productName: string
  optionLabel: string
  qty: number
  unit: string
  salesAmount: number
  platformFee: number
  sellerMargin: number
  shippingFee: number
  payout: number
  confirmedAt: string | null
}

export interface SettlementRequest {
  id: number
  sellerCode: string
  storeName: string
  title: string
  count: number
  salesAmount: number
  platformFee: number
  sellerMargin: number
  shippingFee: number
  payout: number
  status: 'REQUESTED' | 'PAID'
  kind?: 'REQUEST' | 'LEGACY'
  requestedAt: string | null
  paidAt: string | null
  paidBy: string
  items: SettlementRequestItem[]
  settleBank?: string
  settleAccount?: string
  settleHolder?: string
}

export interface SellerSettlementBoard {
  eligible: AdminSettlementItem[]
  requests: SettlementRequest[]
  availableCount: number
  availableSum: number
  requestedCount: number
  requestedSum: number
  paidCount: number
  paidSum: number
}

export async function fetchSellerSettlementBoard(
  query: { period?: string; keyword?: string } = {},
) {
  const params = new URLSearchParams()
  if (query.period) params.set('period', query.period)
  if (query.keyword) params.set('keyword', query.keyword)
  const suffix = params.toString() ? `?${params.toString()}` : ''
  const response = await apiFetch(`${SELLER_BASE}/settlements/board${suffix}`, fetchOptions)
  const result = await parseJson<SellerSettlementBoard>(response)
  return unwrap(result, '정산 가능 목록을 불러오지 못했습니다.')
}

export async function createSellerSettlementRequest(itemIdxs: number[]) {
  const response = await apiFetch(`${SELLER_BASE}/settlements/requests`, {
    ...jsonOptions,
    method: 'POST',
    body: JSON.stringify({ itemIdxs }),
  })
  const result = await parseJson<{ request: SettlementRequest | null }>(response)
  if (!result.success) throw new Error(result.message || '정산 요청에 실패했습니다.')
  return { message: result.message || '정산 요청을 등록했습니다.', request: result.data?.request || null }
}

export async function cancelSellerSettlementRequest(id: number) {
  const response = await apiFetch(`${SELLER_BASE}/settlements/requests/${id}`, {
    ...fetchOptions,
    method: 'DELETE',
  })
  const result = await parseJson<null>(response)
  if (!result.success) throw new Error(result.message || '정산 요청 취소에 실패했습니다.')
  return result.message || '정산 요청을 취소했습니다.'
}

async function downloadSettlementExcel(url: string, filename: string) {
  const response = await apiFetch(url, fetchOptions)
  if (!response.ok) {
    let message = '엑셀을 내려받지 못했습니다.'
    try {
      const json = (await response.json()) as { message?: string }
      if (json?.message) message = json.message
    } catch {
      /* ignore */
    }
    throw new Error(message)
  }
  const blob = await response.blob()
  const objectUrl = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = objectUrl
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(objectUrl)
}

export function downloadSellerSettlementRequestExcel(request: SettlementRequest) {
  return downloadSettlementExcel(
    `${SELLER_BASE}/settlements/requests/${request.id}/excel`,
    `${request.title}.xlsx`,
  )
}

export async function fetchAdminSettlementRequests(
  query: { period?: string; status?: string; keyword?: string } = {},
) {
  const params = new URLSearchParams()
  if (query.period) params.set('period', query.period)
  if (query.status) params.set('status', query.status)
  if (query.keyword) params.set('keyword', query.keyword)
  const suffix = params.toString() ? `?${params.toString()}` : ''
  const response = await apiFetch(`${ADMIN_BASE}/settlements/requests${suffix}`, fetchOptions)
  const result = await parseJson<{
    requests: SettlementRequest[]
    requestedCount: number
    requestedSum: number
  }>(response)
  return unwrap(result, '정산 요청을 불러오지 못했습니다.')
}

export function downloadAdminSettlementRequestExcel(request: SettlementRequest) {
  return downloadSettlementExcel(
    `${ADMIN_BASE}/settlements/requests/${request.id}/excel`,
    `${request.title}.xlsx`,
  )
}

export async function markAdminSettlementRequestPaid(id: number) {
  const response = await apiFetch(`${ADMIN_BASE}/settlements/requests/${id}/pay`, {
    ...jsonOptions,
    method: 'POST',
    body: '{}',
  })
  const result = await parseJson<null>(response)
  if (!result.success) throw new Error(result.message || '입금 처리에 실패했습니다.')
  return result.message || '입금 완료로 표시했습니다.'
}

export async function reopenAdminSettlementRequest(id: number, reason: string) {
  const response = await apiFetch(`${ADMIN_BASE}/settlements/requests/${id}/reopen`, {
    ...jsonOptions,
    method: 'POST',
    body: JSON.stringify({ reason }),
  })
  const result = await parseJson<null>(response)
  if (!result.success) throw new Error(result.message || '입금 해제에 실패했습니다.')
  return result.message || '입금 완료를 해제했습니다.'
}
